// Smart Bidding Learning Period — Bidding & Budgets section, deterministic check.
// Shows per-campaign learning status (fresh / calibrating / settled) with the bidding-related
// fields that changed and how long ago. Auditor's takeaway: don't expect stable performance
// from in-learning campaigns yet, and hold off on further bid-strategy tweaks.

import classNames from 'classnames';

import type { EvidenceProps } from './registry';

type LearningStatus = 'fresh_learning' | 'calibrating' | 'settled';

interface CampaignLearningRow {
  campaignId?: string;
  campaignName?: string;
  biddingStrategy?: string;
  status: LearningStatus;
  daysSinceChange: number | null;
  lastChangeAt: string | null;
  lastChangedFields: string[];
  changeAuthor: string | null;
}

interface LearningShape {
  totalSearchCampaigns: number;
  freshLearningCount: number;
  calibratingCount: number;
  settledCount: number;
  freshLearningWindowDays: number;
  lookbackWindowDays: number;
  campaigns: CampaignLearningRow[];
}

const STRATEGY_LABEL: Record<string, string> = {
  MANUAL_CPC: 'Manual CPC',
  ENHANCED_CPC: 'Enhanced CPC',
  MAXIMIZE_CONVERSIONS: 'Max Conversions',
  MAXIMIZE_CONVERSION_VALUE: 'Max Conv. Value',
  TARGET_CPA: 'Target CPA',
  TARGET_ROAS: 'Target ROAS',
  TARGET_IMPRESSION_SHARE: 'Target Imp. Share',
  TARGET_SPEND: 'Target Spend',
  PERCENT_CPC: 'Percent CPC'
};

function strategyLabel(s: string | undefined): string {
  if (!s) return '—';
  return STRATEGY_LABEL[s] ?? s;
}

const STATUS_LABEL: Record<LearningStatus, string> = {
  fresh_learning: 'In learning',
  calibrating: 'Calibrating',
  settled: 'Settled'
};

// Mostly-friendly rendering of the camelCase API field paths Google returns. Keeps the
// table compact without losing precision.
const FIELD_LABEL: Record<string, string> = {
  biddingStrategyType: 'strategy type',
  biddingStrategy: 'portfolio strategy',
  'targetCpa.targetCpaMicros': 'tCPA value',
  'targetRoas.targetRoas': 'tROAS value',
  'maximizeConversions.targetCpaMicros': 'Max Conv. tCPA',
  'maximizeConversionValue.targetRoas': 'Max Conv. Value tROAS',
  'manualCpc.enhancedCpcEnabled': 'Enhanced CPC',
  'percentCpc.cpcBidCeilingMicros': 'Percent CPC ceiling',
  'targetImpressionShare.locationFractionMicros': 'target IS %',
  targetSpend: 'Target Spend'
};

function fieldLabel(f: string): string {
  return FIELD_LABEL[f] ?? f;
}

function formatDaysAgo(n: number | null): string {
  if (n === null) return '—';
  if (n === 0) return 'today';
  if (n === 1) return '1d ago';
  return `${n}d ago`;
}

export const SmartBiddingLearningPeriodEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as LearningShape;

  return (
    <div className='evidence'>
      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Learning status across campaigns
          <span className='evidence__section-tag evidence__section-tag--context'>
            Last {e.lookbackWindowDays} days
          </span>
        </h4>
        <div className='audience-coverage'>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.freshLearningCount.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>
              In learning (≤ {e.freshLearningWindowDays}d)
            </div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.calibratingCount.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>Calibrating (7–14d)</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.settledCount.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>Settled</div>
          </div>
        </div>
      </section>

      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Per-campaign learning status
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.totalSearchCampaigns} Search campaigns
          </span>
        </h4>
        <table className='evidence__table evidence__table--learning-period'>
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Bidding strategy</th>
              <th>Status</th>
              <th className='evidence__th--right'>Last bid change</th>
              <th>What changed</th>
            </tr>
          </thead>
          <tbody>
            {e.campaigns.map(c => {
              const inLearning = c.status === 'fresh_learning' || c.status === 'calibrating';
              return (
                <tr
                  key={c.campaignId ?? c.campaignName}
                  className={classNames({ 'evidence__row--flagged': inLearning })}
                >
                  <td>{c.campaignName ?? '(unnamed)'}</td>
                  <td>{strategyLabel(c.biddingStrategy)}</td>
                  <td>
                    <span className={`evidence__pill evidence__pill--learning-${c.status}`}>
                      {STATUS_LABEL[c.status]}
                    </span>
                  </td>
                  <td className='evidence__td--right'>{formatDaysAgo(c.daysSinceChange)}</td>
                  <td>
                    {c.lastChangedFields.length === 0 ? (
                      <span className='evidence__inline-note'>—</span>
                    ) : (
                      <span className='evidence__modifier-list'>
                        {c.lastChangedFields.map((f, i) => (
                          <span key={i} className='evidence__audience-chip'>
                            {fieldLabel(f)}
                          </span>
                        ))}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <p className='evidence__footnote'>
        Google's Smart Bidding recalibrates after bidding-strategy or target-value changes — the
        documented learning window is typically <strong>7 days</strong>, sometimes longer for
        high-volume campaigns. Performance during this window isn't representative; further
        bid-strategy tweaks should hold off until learning completes.
      </p>
    </div>
  );
};
